import React, { useState } from 'react';
import { Calendar, ChevronDown, X } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Label } from './Label';
import { Modal } from './Modal';
import { cn } from '../../lib/utils';

interface DateRange {
  from: Date;
  to: Date;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
}

const presets = [
  {
    label: 'Today',
    getValue: () => ({
      from: new Date(),
      to: new Date()
    })
  },
  {
    label: 'Yesterday',
    getValue: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return {
        from: yesterday,
        to: yesterday
      };
    }
  },
  {
    label: 'Last 7 days',
    getValue: () => ({
      from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      to: new Date()
    })
  },
  {
    label: 'Last 30 days',
    getValue: () => ({
      from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      to: new Date()
    })
  },
  {
    label: 'Last 90 days',
    getValue: () => ({
      from: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
      to: new Date()
    })
  },
  {
    label: 'This month',
    getValue: () => {
      const now = new Date();
      return {
        from: new Date(now.getFullYear(), now.getMonth(), 1),
        to: new Date()
      };
    }
  },
  {
    label: 'Last month',
    getValue: () => {
      const now = new Date();
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      return {
        from: lastMonth,
        to: lastDayOfLastMonth
      };
    }
  }
];

export function DateRangePicker({ value, onChange, className }: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customRange, setCustomRange] = useState({
    from: value.from,
    to: value.to
  });

  // Get icon color based on theme
  const getIconColor = () => {
    const isDark = document.documentElement.classList.contains('dark');
    return isDark ? 'rgb(209, 213, 219)' : 'rgb(55, 65, 81)'; // gray-300 for dark, gray-700 for light
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateForInput = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const getDisplayText = () => {
    if (value.from.toDateString() === value.to.toDateString()) {
      return formatDate(value.from);
    }
    return `${formatDate(value.from)} - ${formatDate(value.to)}`;
  };

  const handlePresetSelect = (preset: typeof presets[0]) => {
    const range = preset.getValue();
    onChange(range);
    setIsOpen(false);
  };

  const handleCustomRangeClick = () => {
    setCustomRange({
      from: value.from,
      to: value.to
    });
    setIsOpen(false);
    setShowCustomModal(true);
  };

  const handleCustomRangeApply = () => {
    // Validate that from date is not after to date
    if (customRange.from > customRange.to) {
      alert('Start date cannot be after end date');
      return;
    }
    
    onChange(customRange);
    setShowCustomModal(false);
  };

  const handleCustomRangeCancel = () => {
    setCustomRange({
      from: value.from,
      to: value.to
    });
    setShowCustomModal(false);
  };

  const handleFromDateChange = (dateString: string) => {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      setCustomRange(prev => ({ ...prev, from: date }));
    }
  };

  const handleToDateChange = (dateString: string) => {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      setCustomRange(prev => ({ ...prev, to: date }));
    }
  };

  return (
    <>
      <div className={cn('relative', className)}>
        <Button
          variant="outline"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2"
        >
          <span>{getDisplayText()}</span>
          <ChevronDown className="w-4 h-4" style={{ color: getIconColor() }} />
        </Button>

        {isOpen && (
          <div className="absolute top-full right-0 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
            <div className="p-3">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Select date range</h4>
              <div className="space-y-1">
                {presets.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => handlePresetSelect(preset)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="border-t border-gray-200 dark:border-gray-700 p-3">
              <button
                onClick={handleCustomRangeClick}
                className="w-full px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md"
              >
                Custom range...
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Custom Date Range Modal */}
      <Modal isOpen={showCustomModal} onClose={handleCustomRangeCancel}>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Custom Date Range
            </h3>
            <button
              onClick={handleCustomRangeCancel}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="from-date" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Start Date
              </Label>
              <div className="relative mt-1">
                <Input
                  id="from-date"
                  type="date"
                  value={formatDateForInput(customRange.from)}
                  onChange={(e) => handleFromDateChange(e.target.value)}
                  className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white pr-10"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-full px-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                    onClick={() => {
                      const input = document.getElementById('from-date') as HTMLInputElement;
                      if (input) {
                        input.focus();
                        input.showPicker?.();
                      }
                    }}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="to-date" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                End Date
              </Label>
              <div className="relative mt-1">
                <Input
                  id="to-date"
                  type="date"
                  value={formatDateForInput(customRange.to)}
                  onChange={(e) => handleToDateChange(e.target.value)}
                  className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white pr-10"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-full px-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                    onClick={() => {
                      const input = document.getElementById('to-date') as HTMLInputElement;
                      if (input) {
                        input.focus();
                        input.showPicker?.();
                      }
                    }}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="text-sm text-gray-600 dark:text-gray-400">
              Selected range: {formatDate(customRange.from)} - {formatDate(customRange.to)}
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <Button
              variant="outline"
              onClick={handleCustomRangeCancel}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCustomRangeApply}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Apply Range
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}